import { reactive, ref, watch, Ref, computed, ComputedRef } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import store from '@/renderer/store';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';
import { formatSize, formatSize2Byte } from '@/renderer/scripts/utils/tools/index';
import smh, { SMH } from '@/renderer/scripts/smh';

interface SizeInfo {
  size: string;
  unit: string;
}

interface Handler {
  sizeOptions: {
    label: string;
    value: string;
  }[];
  orgFormData: {
    allowChangeNickname: string;
    enableViewAllOrgUser: string;
  };
  userFormData: SMH.DefaultUserOptions;
  teamFormData: SMH.DefaultTeamOptions;
  orgSpace: Ref<string>;
  userSizeInfo: SizeInfo;
  teamSizeInfo: SizeInfo;
  userSpaceConfig: Ref<string>;
  teamSpaceConfig: Ref<string>;
  currUserSizeMoreThanOrgSize: ComputedRef<boolean>;
  currUserSizeLessThanMinSize: ComputedRef<boolean>;
  minSizeStr: string;
  userSizeDisabled: ComputedRef<boolean>;
  userSaveDisabled: ComputedRef<boolean>;
  orgSaveDisabled: ComputedRef<boolean>;
  currTeamSizeMoreThanOrgSize: ComputedRef<boolean>;
  teamSizeDisabled: ComputedRef<boolean>;
  teamSaveDisabled: ComputedRef<boolean>;
  columns: Array<any>;
  roleList: ComputedRef<any[]>;
  showAddRole: Ref<boolean>;
  roleShowType: Ref<string>;
  modifyData: Ref<SMH.CustomRole | null>;
  methods: {
    getOrgSpaceInfo: () => Promise<void>;
    saveOrgSettings: () => Promise<void>;
    saveUserSettings: () => Promise<void>;
    saveTeamSettings: () => Promise<void>;
    modifyRole: (val: any) => void;
    createRole: () => void;
  };
}

export default function pullListHandler(): Handler {
  const columns = [
    {
      colKey: 'name',
      title: i18n_t('角色'),
      width: 105,
      align: 'center',
      ellipsis: true,
    },
    {
      colKey: 'canView',
      title: i18n_t('查看列表'),
      width: 80,
      align: 'center',
    },
    {
      colKey: 'canPreview',
      title: i18n_t('预览'),
      width: i18n.locale === 'zh-CN' ? 80 : 90,
      align: 'center',
    },
    {
      colKey: 'canDownload',
      title: i18n_t('下载'),
      width: i18n.locale === 'zh-CN' ? 80 : 100,
      align: 'center',
    },
    {
      colKey: 'canPrint',
      title: i18n_t('打印'),
      width: 80,
      align: 'center',
    },
    {
      colKey: 'canUpload',
      title: i18n_t('上传'),
      width: 80,
      align: 'center',
    },
    {
      colKey: 'canDelete',
      title: i18n_t('删除'),
      width: 80,
      align: 'center',
    },
    {
      colKey: 'canModify',
      title: i18n_t('修改'),
      width: 80,
      align: 'center',
    },
    {
      colKey: 'canAuthorize',
      title: i18n_t('共享'),
      width: 80,
      align: 'center',
    },
    {
      colKey: 'canShare',
      title: i18n_t('分享'),
      width: 80,
      align: 'center',
    },
    {
      colKey: 'operate',
      title: '',
      width: 60,
      align: 'center',
    },
  ];
  const { sizeMap } = ConstantConfig;
  const sizeOptions = Object.keys(sizeMap).map(key => ({
    label: key,
    value: key,
  }));
  const showAddRole = ref(false);
  const roleShowType = ref('add');
  const modifyData = ref<SMH.CustomRole | null>(null);
  const defaultUser = computed(() => store.getters.defaultUserOptions);
  const defaultAllowChangeNickname = computed(() => store.getters.allowChangeNickname);
  const defaultEnableViewAllOrgUser = computed(() => store.getters.enableViewAllOrgUser);
  const defaultTeam = computed(() => store.getters.defaultTeamOptions);
  const roleList = computed(() => store.getters.roles);
  const userFormData = reactive({
    enabled: defaultUser.value.enabled ? 'true' : 'false',
    allowPersonalSpace: defaultUser.value.allowPersonalSpace,
    personalSpaceQuotaSize: defaultUser.value.personalSpaceQuotaSize,
  });
  const orgFormData = reactive({
    allowChangeNickname: defaultAllowChangeNickname.value,
    enableViewAllOrgUser: defaultEnableViewAllOrgUser.value,
  });
  const teamFormData = reactive({
    defaultRoleId: defaultTeam.value.defaultRoleId,
    spaceQuotaSize: defaultTeam.value.spaceQuotaSize,
  });
  const userSizeInfo = reactive({
    size: `${formatSize(defaultUser.value.personalSpaceQuotaSize, true).size}`,
    unit: formatSize(defaultUser.value.personalSpaceQuotaSize, true).unit,
  });
  const teamSizeInfo = reactive({
    size: `${formatSize(defaultTeam.value.spaceQuotaSize || ConstantConfig.defaultTeamSpaceSize, true).size}`,
    unit: formatSize(defaultTeam.value.spaceQuotaSize || ConstantConfig.defaultTeamSpaceSize, true).unit,
  });

  // 空间大小选项 'shared' | 'customed'
  const userSpaceConfig = ref(!defaultUser.value.personalSpaceQuotaSize ? 'shared' : 'customed');
  const teamSpaceConfig = ref(!defaultTeam.value.spaceQuotaSize ? 'shared' : 'customed');

  // 企业剩余配额
  const orgSpaceSize = ref('-1');
  const orgSpace = ref('');
  const minSizeStr = `${formatSize(ConstantConfig.minPersonalSpaceSize).size}${
    formatSize(ConstantConfig.minPersonalSpaceSize).unit
  }`;

  // 用户空间大小校验
  // 当前输入的空间大于企业剩余空间大小
  const currUserSizeMoreThanOrgSize = computed(() => {
    const currSize = formatSize2Byte(userSizeInfo.size, userSizeInfo.unit);
    return +orgSpaceSize.value > -1 && currSize > +orgSpaceSize.value;
  });
  // 当前输入的空间小于个人空间最小额度
  const currUserSizeLessThanMinSize = computed(() => {
    const currSize = formatSize2Byte(userSizeInfo.size, userSizeInfo.unit);
    return currSize < ConstantConfig.minPersonalSpaceSize;
  });
  const userSpaceConfigChanged = computed(() => {
    const initSpaceConfig = !defaultUser.value.personalSpaceQuotaSize ? 'shared' : 'customed';
    return initSpaceConfig !== userSpaceConfig.value;
  });
  const userSizeChanged = computed(() => {
    const currNum = formatSize2Byte(userSizeInfo.size, userSizeInfo.unit);
    const initNum = defaultUser.value.personalSpaceQuotaSize;
    return +initNum !== currNum;
  });
  const userSizeDisabled = computed(() => {
    // 这个computed只判断是否更改了大小
    if (!orgSpaceSize.value || !userSizeChanged.value) return +userSizeInfo.size === 0;
    return +userSizeInfo.size === 0 || currUserSizeMoreThanOrgSize.value || currUserSizeLessThanMinSize.value;
  });
  const orgSaveDisabled = computed(() => {
    // 只判断是否有数据和初始数据不一致
    const enabledChanged = orgFormData.allowChangeNickname === defaultAllowChangeNickname.value;
    const enableViewAllOrgUser = orgFormData.enableViewAllOrgUser === defaultEnableViewAllOrgUser.value;
    return enabledChanged && enableViewAllOrgUser;
  });
  const userSaveDisabled = computed(() => {
    // 只判断是否有数据和初始数据不一致
    const enabledChanged = (userFormData.enabled === 'true') !== defaultUser.value.enabled;
    const allowPersonalSpaceChanged = userFormData.allowPersonalSpace !== defaultUser.value.allowPersonalSpace;
    const sizeChanged =
      userSpaceConfigChanged.value ||
      (userSpaceConfig.value === 'customed' &&
        `${formatSize2Byte(`${userSizeInfo.size}`, userSizeInfo.unit)}` !== defaultUser.value.personalSpaceQuotaSize);
    return (
      !(enabledChanged || allowPersonalSpaceChanged || sizeChanged) ||
      (userSpaceConfig.value === 'customed' && userSizeDisabled.value)
    );
  });

  // 团队空间大小校验
  // 当前输入的空间大于企业剩余空间大小
  const currTeamSizeMoreThanOrgSize = computed(() => {
    const currSize = formatSize2Byte(teamSizeInfo.size, teamSizeInfo.unit);
    return +orgSpaceSize.value > -1 && currSize > +orgSpaceSize.value;
  });
  const teamSpaceConfigChanged = computed(() => {
    const initNum = defaultTeam.value.spaceQuotaSize;
    const initSpaceConfig = !initNum ? 'shared' : 'customed';
    return initSpaceConfig !== teamSpaceConfig.value;
  });
  const teamSizeChanged = computed(() => {
    const currNum = formatSize2Byte(teamSizeInfo.size, teamSizeInfo.unit);
    const initNum = defaultTeam.value.spaceQuotaSize;
    const changed = +initNum !== currNum;
    return changed;
  });
  const teamSizeIsNull = computed(() => teamSizeInfo.size === '0' || +teamSizeInfo.size === 0);
  const teamSizeDisabled = computed(() => {
    if (!orgSpaceSize.value) return teamSizeIsNull.value;
    if (!teamSizeChanged.value) return teamSizeIsNull.value;
    return teamSpaceConfig.value === 'customed' && (teamSizeIsNull.value || currTeamSizeMoreThanOrgSize.value);
  });
  const teamSaveDisabled = computed(() => {
    const defaultRoleIdChanged = teamFormData.defaultRoleId !== defaultTeam.value.defaultRoleId;
    const spaceQuotaSizeChanged =
      teamSpaceConfigChanged.value ||
      (teamSpaceConfig.value === 'customed' &&
        `${formatSize2Byte(`${teamSizeInfo.size}`, teamSizeInfo.unit)}` !== defaultTeam.value.spaceQuotaSize);
    return (!defaultRoleIdChanged && !spaceQuotaSizeChanged) || teamSizeDisabled.value;
  });
  const methods = {
    // 查询企业剩余配额
    async getOrgSpaceInfo() {
      try {
        const { availableSpace } = await store.dispatch('GetOrgSpace');
        orgSpaceSize.value = `${availableSpace}`;
        const sizeInfo = formatSize(orgSpaceSize.value);
        orgSpace.value = `${sizeInfo.size}${sizeInfo.unit}`;
      } catch (e) {
        orgSpaceSize.value = '0';
        orgSpace.value = '0MB';
      }
    },
    // 保存组织设置
    async saveOrgSettings() {
      try {
        await smh.userAPI.updateOrgInfo({
          extensionData: {
            allowChangeNickname: orgFormData.allowChangeNickname,
            enableViewAllOrgUser: orgFormData.enableViewAllOrgUser,
          },
        });
        store.commit('SET_ALLOW_CHANGE_NICKNAME', orgFormData.allowChangeNickname);
        store.commit('SET_VIEW_ALL_ORG_USER', orgFormData.enableViewAllOrgUser);
        tips.success(i18n_t('修改成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    // 保存用户设置
    async saveUserSettings() {
      const personalSpaceQuotaSize =
        userSpaceConfig.value === 'shared' ? null : `${formatSize2Byte(`${userSizeInfo.size}`, userSizeInfo.unit)}`;
      try {
        const options = {
          enabled: userFormData.enabled === 'true',
          allowPersonalSpace: userFormData.allowPersonalSpace,
          personalSpaceQuotaSize,
        };
        await smh.userAPI.updateOrgInfo({
          extensionData: {
            defaultUserOptions: options,
          },
        });
        store.commit('SET_USER_OPTIONS', options);
        tips.success(i18n_t('修改成功'));
        reporter.report('initialize_user_settings', {
          default_setting: userFormData.enabled === 'true' ? 1 : 0,
          personal_space_setting: userFormData.allowPersonalSpace ? 1 : 0,
          personal_space_detail: `${personalSpaceQuotaSize}`,
        });
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    // 保存团队设置
    async saveTeamSettings() {
      const spaceQuotaSize =
        teamSpaceConfig.value === 'shared' ? null : `${formatSize2Byte(`${teamSizeInfo.size}`, teamSizeInfo.unit)}`;
      try {
        const options = {
          defaultRoleId: teamFormData.defaultRoleId,
          spaceQuotaSize,
        };
        await smh.userAPI.updateOrgInfo({
          extensionData: {
            defaultTeamOptions: options,
          },
        });
        store.commit('SET_TEAM_OPTIONS', options);
        tips.success(i18n_t('修改成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    createRole() {
      showAddRole.value = true;
      roleShowType.value = 'add';
    },
    modifyRole(val: SMH.CustomRole) {
      showAddRole.value = true;
      roleShowType.value = 'modify';
      modifyData.value = val;
    },
  };
  watch(defaultAllowChangeNickname, val => {
    orgFormData.allowChangeNickname = val;
  });
  watch(defaultEnableViewAllOrgUser, val => {
    orgFormData.enableViewAllOrgUser = val;
  });
  watch(defaultUser, val => {
    const sizeInfo = formatSize(val.personalSpaceQuotaSize, true);
    userSpaceConfig.value = !val.personalSpaceQuotaSize ? 'shared' : 'customed';
    Object.assign(userSizeInfo, {
      size: sizeInfo.size,
      unit: sizeInfo.unit,
    });
    Object.assign(userFormData, {
      enabled: val.enabled ? 'true' : 'false',
      allowPersonalSpace: val.allowPersonalSpace,
      personalSpaceQuotaSize: val.personalSpaceQuotaSize,
    });
  });
  watch(defaultTeam, val => {
    const sizeInfo = formatSize(val.spaceQuotaSize || ConstantConfig.defaultTeamSpaceSize, true);
    teamSpaceConfig.value = !val.spaceQuotaSize ? 'shared' : 'customed';
    Object.assign(teamSizeInfo, {
      size: sizeInfo.size,
      unit: sizeInfo.unit,
    });
    Object.assign(teamFormData, {
      defaultRoleId: val.defaultRoleId,
      spaceQuotaSize: val.spaceQuotaSize,
    });
  });
  return {
    sizeOptions,
    orgFormData,
    userFormData,
    teamFormData,
    orgSpace,
    userSizeInfo,
    teamSizeInfo,
    userSpaceConfig,
    teamSpaceConfig,
    currUserSizeMoreThanOrgSize,
    currUserSizeLessThanMinSize,
    minSizeStr,
    userSizeDisabled,
    userSaveDisabled,
    orgSaveDisabled,
    currTeamSizeMoreThanOrgSize,
    teamSizeDisabled,
    teamSaveDisabled,
    columns,
    roleList,
    showAddRole,
    roleShowType,
    modifyData,
    methods,
  };
}
